import React, { useEffect, useState } from 'react';
import { 
  Form, 
  Input, 
  InputNumber, 
  Button, 
  message, 
  Space,
  Divider,
  Tooltip
} from 'antd';
import { 
  SaveOutlined, 
  ArrowLeftOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PageTitle, StyledCard, FormLabel, SearchableSelect } from '../common';
import { theme } from '../../theme';

// Interfaces
interface RetencionFormData {
  descripcion: string;
  porcentaje: number;
  portonelada: number;
  tipo: string;
  minimo_porton: number;
  minimo_pesos: number;
  acumulado_ganancia: number;
  provincia: number | null;
  aplicar: string;
  alta: number;
}

interface Provincia {
  id: number;
  provincia: string;
}

const AltaRetenciones: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();

  // Cargar provincias al inicializar
  useEffect(() => {
    if (user?.id_empresa) {
      fetchProvincias();
    }
  }, [user]);

  const fetchProvincias = async () => {
    setLoadingProvincias(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get<Provincia[]>(
        `${process.env.REACT_APP_COMMON_URL}/listar_provincias/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setProvincias(response.data);
    } catch (error: any) {
      console.error('Error al cargar provincias:', error);
      message.error('Error al cargar la lista de provincias');
    } finally {
      setLoadingProvincias(false);
    }
  };

  const handleSubmit = async (values: RetencionFormData) => {
    if (!user?.id_empresa) {
      message.error('No se pudo obtener la información de la empresa');
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      
      const retencionData = {
        ...values,
        empresa: user.id_empresa,
        alta: 1 // Siempre activo al crear
      };

      const response = await axios.post(
        `${process.env.REACT_APP_LIQUIDACION_URL}/crear_retencion/${user.id_empresa}/`,
        retencionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      message.success('Retención creada exitosamente');
      form.resetFields();
      
      // Opcional: navegar de vuelta al listado
      // navigate('/liquidacion/retenciones');
      
    } catch (error: any) {
      console.error('Error al crear retención:', error);
      if (error.response?.data?.error) {
        message.error(`Error: ${error.response.data.error}`);
      } else {
        message.error('Error al crear la retención');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVolver = () => {
    navigate('/liquidacion/retenciones');
  };

  const handleLimpiar = () => {
    form.resetFields();
  };

  return (
    <div style={{ padding: '0' }}>
      <PageTitle style={{ marginBottom: '12px', fontSize: '24px' }}>Retenciones/Percepciones</PageTitle>

      <StyledCard title="Información de la Retención">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            tipo: 'IVA',
            aplicar: 'COMPRA/SERVICIO',
            alta: 1,
            porcentaje: 0,
            portonelada: 0,
            minimo_porton: 0,
            minimo_pesos: 0,
            acumulado_ganancia: 0
          }}
          style={{ 
            maxWidth: '100%',
            padding: '12px'
          }}
          className="retenciones-form"
        >
          <style>{`
            .retenciones-form .ant-form-item {
              margin-bottom: 8px !important;
            }
            .retenciones-form .ant-form-item-label > label {
              color: ${theme.colors.textPrimary} !important;
              font-weight: 500 !important;
              font-size: 12px !important;
              line-height: 1.3 !important;
            }
            .retenciones-form .ant-form-item-required::before {
              color: ${theme.colors.error} !important;
            }
            .retenciones-form .ant-input,
            .retenciones-form .ant-select-selector,
            .retenciones-form .ant-input-number {
              border-color: ${theme.colors.border} !important;
              border-radius: ${theme.borderRadius.md} !important;
              font-size: 13px !important;
              height: 32px !important;
              padding: 4px 11px !important;
            }
            .retenciones-form .ant-input-number-input {
              height: 30px !important;
              font-size: 13px !important;
            }
            .retenciones-form .ant-select-selector {
              height: 32px !important;
            }
            .retenciones-form .ant-select-selection-item,
            .retenciones-form .ant-select-selection-placeholder {
              line-height: 30px !important;
              font-size: 13px !important;
            }
            .retenciones-form .ant-input:focus,
            .retenciones-form .ant-select-selector:focus,
            .retenciones-form .ant-input-number:focus {
              border-color: ${theme.colors.primary} !important;
              box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
            }
          `}</style>

          {/* Grid de 3 columnas - Información básica */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            <Form.Item
              label={<FormLabel required>Descripción</FormLabel>}
              name="descripcion"
              rules={[
                { required: true, message: 'Por favor ingrese la descripción' },
                { max: 100, message: 'La descripción no puede exceder 100 caracteres' }
              ]}
            >
              <Input 
                placeholder="Ej: Retención IVA" 
              />
            </Form.Item>

            <Form.Item
              label={
                <FormLabel required>
                  <Space>
                    Tipo de Retención
                    <Tooltip 
                      title="Seleccione el tipo de retención según corresponda. IVA: Impuesto al Valor Agregado, GANANCIA: Impuesto a las Ganancias, RENTAS: Impuesto a las Rentas, OTROS: Otros tipos de retenciones."
                      placement="top"
                    >
                      <QuestionCircleOutlined style={{ color: theme.colors.primary, cursor: 'help' }} />
                    </Tooltip>
                  </Space>
                </FormLabel>
              }
              name="tipo"
              rules={[{ required: true, message: 'Por favor seleccione el tipo' }]}
            >
              <SearchableSelect 
                placeholder="Seleccionar tipo"
                options={[
                  { value: 'IVA', label: 'IVA' },
                  { value: 'GANANCIA', label: 'GANANCIA' },
                  { value: 'RENTAS', label: 'RENTAS' },
                  { value: 'OTROS', label: 'OTROS' }
                ]}
              />
            </Form.Item>

            <Form.Item
              label={
                <FormLabel>
                  <Space>
                    Provincia
                    <Tooltip 
                      title="Seleccione la provincia donde aplicará la retención. Este campo es opcional y se utiliza para retenciones específicas por jurisdicción."
                      placement="top"
                    >
                      <QuestionCircleOutlined style={{ color: theme.colors.primary, cursor: 'help' }} />
                    </Tooltip>
                  </Space>
                </FormLabel>
              }
              name="provincia"
            >
              <SearchableSelect 
                placeholder="Seleccionar provincia (opcional)"
                allowClear
                loading={loadingProvincias}
                options={provincias.map(provincia => ({
                  value: provincia.id,
                  label: provincia.provincia
                }))}
              />
            </Form.Item>
          </div>

          <Divider orientation="left" plain style={{ marginTop: '8px', marginBottom: '8px', fontSize: '13px' }}>
            Valores Monetarios
          </Divider>

          {/* Grid de 4 columnas - Valores monetarios */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <Form.Item
              label={
                <FormLabel required>
                  <Space>
                    Porcentaje (%)
                    <Tooltip 
                      title="Ingrese el porcentaje de retención que se aplicará. Debe ser un valor entre 0 y 100. Ejemplo: 21% para IVA."
                      placement="top"
                    >
                      <QuestionCircleOutlined style={{ color: theme.colors.primary, cursor: 'help' }} />
                    </Tooltip>
                  </Space>
                </FormLabel>
              }
              name="porcentaje"
              rules={[
                { required: true, message: 'Por favor ingrese el porcentaje' },
                { type: 'number', min: 0, max: 100, message: 'El porcentaje debe estar entre 0 y 100' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                max={100}
                step={0.01}
                precision={2}
                formatter={value => `${value}%`}
                parser={value => value!.replace('%', '') as any}
              />
            </Form.Item>

            <Form.Item
              label={
                <FormLabel required>
                  <Space>
                    Portón por Tonelada
                    <Tooltip 
                      title="Monto fijo que se cobra por cada tonelada de algodón. Este valor se suma al porcentaje de retención calculado."
                      placement="top"
                    >
                      <QuestionCircleOutlined style={{ color: theme.colors.primary, cursor: 'help' }} />
                    </Tooltip>
                  </Space>
                </FormLabel>
              }
              name="portonelada"
              rules={[
                { required: true, message: 'Por favor ingrese el portón por tonelada' },
                { type: 'number', min: 0, message: 'El valor debe ser mayor o igual a 0' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                step={0.01}
                precision={2}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>

            <Form.Item
              label={<FormLabel required>Mínimo Portón</FormLabel>}
              name="minimo_porton"
              rules={[
                { required: true, message: 'Por favor ingrese el mínimo portón' },
                { type: 'number', min: 0, message: 'El valor debe ser mayor o igual a 0' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                step={0.01}
                precision={2}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>

            <Form.Item
              label={<FormLabel required>Mínimo Pesos</FormLabel>}
              name="minimo_pesos"
              rules={[
                { required: true, message: 'Por favor ingrese el mínimo en pesos' },
                { type: 'number', min: 0, message: 'El valor debe ser mayor o igual a 0' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                step={0.01}
                precision={2}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>
          </div>

          {/* Grid de 2 columnas - Campos finales */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginTop: '8px' }}>
            <Form.Item
              label={<FormLabel required>Acumulado Ganancia</FormLabel>}
              name="acumulado_ganancia"
              rules={[
                { required: true, message: 'Por favor ingrese el acumulado de ganancia' },
                { type: 'number', min: 0, message: 'El valor debe ser mayor o igual a 0' }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                step={0.01}
                precision={2}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>

            <Form.Item
              label={
                <FormLabel required>
                  <Space>
                    Aplicar a
                    <Tooltip 
                      title="Seleccione a qué tipo de operación se aplicará la retención. COMPRA/SERVICIO: Se aplica a ambos, COMPRA: Solo a compras, SERVICIO: Solo a servicios."
                      placement="top"
                    >
                      <QuestionCircleOutlined style={{ color: theme.colors.primary, cursor: 'help' }} />
                    </Tooltip>
                  </Space>
                </FormLabel>
              }
              name="aplicar"
              rules={[{ required: true, message: 'Por favor seleccione a qué aplicar' }]}
            >
              <SearchableSelect 
                placeholder="Seleccionar aplicación"
                options={[
                  { value: 'COMPRA/SERVICIO', label: 'COMPRA/SERVICIO' },
                  { value: 'COMPRA', label: 'COMPRA' },
                  { value: 'SERVICIO', label: 'SERVICIO' }
                ]}
              />
            </Form.Item>
          </div>

          {/* Botones de acción */}
          <Divider style={{ marginTop: '12px', marginBottom: '8px' }} />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '6px',
            marginTop: '8px'
          }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleVolver}
              size="small"
              style={{
                color: theme.colors.textSecondary,
                borderColor: theme.colors.border,
                fontSize: '12px',
                height: '28px',
                padding: '0 12px'
              }}
            >
              Volver al Listado
            </Button>

            <Button
              onClick={handleLimpiar}
              size="small"
              style={{
                color: theme.colors.warning,
                borderColor: theme.colors.warning,
                fontSize: '12px',
                height: '28px',
                padding: '0 12px'
              }}
            >
              Limpiar Formulario
            </Button>

            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              size="small"
              style={{
                backgroundColor: theme.colors.primary,
                borderColor: theme.colors.primary,
                fontSize: '12px',
                height: '28px',
                padding: '0 12px'
              }}
            >
              Grabar Retención
            </Button>
          </div>
        </Form>
      </StyledCard>
    </div>
  );
};

export default AltaRetenciones;
